// MembersPage — Phase 5 Wave 2 + Wave 2.6 (v2-cp invitations integration).
//
// Endpoints consumed:
//
//   GET    /api/v1/organizations/{id}/members          — BE-17
//   PATCH  /api/v1/organizations/{id}/members/{mid}    — BE-18 (role)
//   DELETE /api/v1/organizations/{id}/members/{mid}    — BE-19 (revoke)
//   GET    /api/v1/organizations/{id}/invitations      — BE-12
//   POST   /api/v1/organizations/{id}/invitations      — BE-11
//   POST   /api/v1/organizations/{id}/invitations/{iid}/resend — BE-13
//   DELETE /api/v1/organizations/{id}/invitations/{iid}        — BE-14
//
// Scope deltas vs legacy:
//
//   - Roles narrow from {owner, admin, billing, developer, viewer} to
//     {owner, admin, viewer}. The billing/developer roles were never
//     wired into v2 scope policy, so this matches what cp-api actually
//     enforces.
//   - {memberId} URL param is the OrgAdmin.ID (cp-internal stable id),
//     NOT the Aegis identity sub.
//   - Owner-transfer modal is HIDDEN until BE-20a/b ships in Wave 3.
//   - Invitation `token` is returned to org-admin callers but is NOT
//     surfaced in the UI. Acceptance flow uses the token via the email
//     link or the invite-accept page — never via the dashboard.

import type { components } from '@intelliauth/api-client-v2-cp/types'
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@intelliauth/ui'
import type { DataTableColumn } from '@intelliauth/ui'
import {
  ChevronDown,
  Clock,
  Crown,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'

type Member = components['schemas']['Member']
type Role = Member['role']
type Invitation = components['schemas']['Invitation']
type InvitationRole = Invitation['role']

const EDITABLE_ROLES: Exclude<Role, 'owner'>[] = ['admin', 'viewer']
const INVITE_ROLES: InvitationRole[] = ['admin', 'viewer']

type Tab = 'members' | 'invitations'

const roleBadgeColors: Record<string, string> = {
  owner: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  viewer: 'bg-foreground/5 text-gray-400 border-border',
}

const statusBadgeColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  accepted: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  expired: 'bg-foreground/5 text-gray-400 border-border',
  revoked: 'bg-red-500/20 text-red-400 border-red-500/30',
}

function readErr(err: unknown, fallback: string): string {
  if (err instanceof CpV2ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export default function MembersPage() {
  const { user, currentOrg } = useAuth()

  const [activeTab, setActiveTab] = useState<Tab>('members')
  const [members, setMembers] = useState<Member[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [showRemoveModal, setShowRemoveModal] = useState<Member | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState<Invitation | null>(null)

  const canManage = currentOrg?.role === 'owner' || currentOrg?.role === 'admin'

  const fetchData = useCallback(async () => {
    if (!currentOrg) return
    setLoading(true)
    try {
      const [membersEnv, invitationsEnv] = await Promise.all([
        cpClient().get('/api/v1/organizations/{id}/members', { path: { id: currentOrg.id } }),
        canManage
          ? cpClient().get('/api/v1/organizations/{id}/invitations', {
              path: { id: currentOrg.id },
            })
          : Promise.resolve({ data: [] as Invitation[] } as unknown),
      ])
      setMembers((membersEnv as { data: Member[] }).data ?? [])
      setInvitations((invitationsEnv as { data: Invitation[] }).data ?? [])
    } catch (err) {
      toast.error(readErr(err, 'Failed to load members'))
    } finally {
      setLoading(false)
    }
  }, [currentOrg, canManage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Pending invitations badge counter — keep recomputed from state.
  const pendingInvitationCount = useMemo(
    () => invitations.filter((inv) => inv.status === 'pending').length,
    [invitations],
  )

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">Members</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage who has access to your organization.
          </p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowInviteModal(true)}
            leftIcon={Plus}
          >
            Invite member
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('members')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'members'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Members ({members.length})
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => setActiveTab('invitations')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'invitations'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Pending invitations ({pendingInvitationCount})
            </button>
          )}
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
        </div>
      ) : activeTab === 'members' ? (
        <MembersList
          members={members}
          currentUserId={user?.id ?? ''}
          canManage={canManage}
          onRoleChange={async (member, newRole) => {
            try {
              await cpClient().patch('/api/v1/organizations/{id}/members/{memberId}', {
                path: { id: currentOrg.id, memberId: member.id },
                body: { role: newRole },
              })
              toast.success(`Updated ${member.email} to ${newRole}`)
              await fetchData()
            } catch (err) {
              toast.error(readErr(err, 'Failed to update role'))
            }
          }}
          onRemove={(member) => setShowRemoveModal(member)}
        />
      ) : (
        <InvitationsList
          invitations={invitations}
          onRevoke={(inv) => setShowRevokeModal(inv)}
          onResend={async (inv) => {
            try {
              await cpClient().post(
                '/api/v1/organizations/{id}/invitations/{invitationId}/resend',
                {
                  path: { id: currentOrg.id, invitationId: inv.id },
                },
              )
              toast.success(`Invitation resent to ${inv.email}`)
              await fetchData()
            } catch (err) {
              toast.error(readErr(err, 'Failed to resend invitation'))
            }
          }}
        />
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          orgId={currentOrg.id}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false)
            setActiveTab('invitations')
            fetchData()
          }}
        />
      )}

      {/* Remove Member Modal */}
      {showRemoveModal && (
        <ConfirmModal
          title="Remove member"
          description={
            <>
              Are you sure you want to remove{' '}
              <strong className="text-foreground">{showRemoveModal.email}</strong> from this
              organization? They will lose access immediately.
            </>
          }
          confirmLabel="Remove"
          variant="danger"
          onClose={() => setShowRemoveModal(null)}
          onConfirm={async () => {
            try {
              await cpClient().delete('/api/v1/organizations/{id}/members/{memberId}', {
                path: { id: currentOrg.id, memberId: showRemoveModal.id },
              })
              toast.success(`Removed ${showRemoveModal.email}`)
              setShowRemoveModal(null)
              await fetchData()
            } catch (err) {
              toast.error(readErr(err, 'Failed to remove member'))
            }
          }}
        />
      )}

      {/* Revoke Invitation Modal */}
      {showRevokeModal && (
        <ConfirmModal
          title="Revoke invitation"
          description={
            <>
              Revoke the invitation for{' '}
              <strong className="text-foreground">{showRevokeModal.email}</strong>? They will no
              longer be able to join using this invitation link.
            </>
          }
          confirmLabel="Revoke"
          variant="danger"
          onClose={() => setShowRevokeModal(null)}
          onConfirm={async () => {
            try {
              await cpClient().delete('/api/v1/organizations/{id}/invitations/{invitationId}', {
                path: { id: currentOrg.id, invitationId: showRevokeModal.id },
              })
              toast.success(`Invitation for ${showRevokeModal.email} revoked`)
              setShowRevokeModal(null)
              await fetchData()
            } catch (err) {
              toast.error(readErr(err, 'Failed to revoke invitation'))
            }
          }}
        />
      )}
    </div>
  )
}

// ── Members List ──────────────────────────────────────────────

function MembersList({
  members,
  currentUserId,
  canManage,
  onRoleChange,
  onRemove,
}: {
  members: Member[]
  currentUserId: string
  canManage: boolean
  onRoleChange: (member: Member, role: Exclude<Role, 'owner'>) => Promise<void>
  onRemove: (member: Member) => void
}) {
  const columns: DataTableColumn<Member>[] = [
    {
      key: 'member',
      header: 'Member',
      cell: (member) => {
        const isCurrentUser = !!member.user_id && member.user_id === currentUserId
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-medium">
              {(member.email?.[0] ?? '?').toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {member.full_name || member.email}
                {isCurrentUser && (
                  <span className="ml-2 text-xs text-muted-foreground/70">(you)</span>
                )}
              </p>
              {member.full_name && (
                <p className="text-xs text-muted-foreground/70">{member.email}</p>
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: 'role',
      header: 'Role',
      width: '160px',
      cell: (member) => (
        <MemberRoleCell
          member={member}
          isCurrentUser={!!member.user_id && member.user_id === currentUserId}
          canManage={canManage}
          onRoleChange={onRoleChange}
        />
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      width: '160px',
      cell: (member) => (
        <span className="text-sm text-muted-foreground">
          {member.joined_at ? formatDate(member.joined_at) : '—'}
        </span>
      ),
    },
  ]

  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      width: '80px',
      align: 'right',
      cell: (member) => {
        const isCurrentUser = !!member.user_id && member.user_id === currentUserId
        if (isCurrentUser || member.role === 'owner') return null
        return (
          <button
            type="button"
            onClick={() => onRemove(member)}
            title="Remove member"
            className="p-1.5 text-muted-foreground/70 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )
      },
    })
  }

  return (
    <DataTable<Member>
      columns={columns}
      rows={members}
      getRowId={(m) => m.id}
      empty={
        <EmptyState
          icon={Users}
          title="No members yet"
          description="Invite teammates to manage this organization with you."
        />
      }
    />
  )
}

function MemberRoleCell({
  member,
  isCurrentUser,
  canManage,
  onRoleChange,
}: {
  member: Member
  isCurrentUser: boolean
  canManage: boolean
  onRoleChange: (member: Member, role: Exclude<Role, 'owner'>) => Promise<void>
}) {
  const [changingRole, setChangingRole] = useState(false)
  const handle = async (newRole: Exclude<Role, 'owner'>) => {
    if (newRole === member.role) return
    setChangingRole(true)
    try {
      await onRoleChange(member, newRole)
    } finally {
      setChangingRole(false)
    }
  }
  if (canManage && !isCurrentUser && member.role !== 'owner') {
    return (
      <div className="relative inline-block">
        <select
          value={member.role}
          onChange={(e) => handle(e.target.value as Exclude<Role, 'owner'>)}
          disabled={changingRole}
          className={`appearance-none pr-7 pl-3 py-1 text-xs font-medium rounded-full border cursor-pointer ${
            roleBadgeColors[member.role] ?? roleBadgeColors.viewer
          } ${changingRole ? 'opacity-50' : ''}`}
        >
          {EDITABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none" />
      </div>
    )
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border ${
        roleBadgeColors[member.role] ?? roleBadgeColors.viewer
      }`}
    >
      {member.role === 'owner' && <Crown className="h-3 w-3" />}
      {member.role}
    </span>
  )
}

// ── Invitations List ──────────────────────────────────────────

function InvitationsList({
  invitations,
  onRevoke,
  onResend,
}: {
  invitations: Invitation[]
  onRevoke: (inv: Invitation) => void
  onResend: (inv: Invitation) => Promise<void>
}) {
  const columns: DataTableColumn<Invitation>[] = [
    {
      key: 'email',
      header: 'Email',
      cell: (inv) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground/70 text-sm font-medium">
            <Mail className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium text-foreground">{inv.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      width: '120px',
      cell: (inv) => (
        <span
          className={`inline-flex px-3 py-1 text-xs font-medium rounded-full border ${
            roleBadgeColors[inv.role] ?? roleBadgeColors.viewer
          }`}
        >
          {inv.role}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      cell: (inv) => (
        <span
          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border ${
            statusBadgeColors[inv.status] ?? statusBadgeColors.pending
          }`}
        >
          <Clock className="h-3 w-3" />
          {inv.status}
        </span>
      ),
    },
    {
      key: 'expires',
      header: 'Expires',
      width: '160px',
      cell: (inv) => (
        <span className="text-sm text-muted-foreground">{formatDate(inv.expires_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      align: 'right',
      cell: (inv) =>
        inv.status === 'pending' ? (
          <div className="flex items-center justify-end gap-1">
            <ResendButton invitation={inv} onResend={onResend} />
            <button
              type="button"
              onClick={() => onRevoke(inv)}
              title="Revoke invitation"
              className="p-1.5 text-muted-foreground/70 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ) : null,
    },
  ]

  return (
    <DataTable<Invitation>
      columns={columns}
      rows={invitations}
      getRowId={(inv) => inv.id}
      empty={
        <EmptyState
          icon={Mail}
          title="No pending invitations"
          description="Invitations you send will appear here until accepted or revoked."
        />
      }
    />
  )
}

function ResendButton({
  invitation,
  onResend,
}: {
  invitation: Invitation
  onResend: (inv: Invitation) => Promise<void>
}) {
  const [sending, setSending] = useState(false)

  const handleResend = async () => {
    setSending(true)
    try {
      await onResend(invitation)
    } finally {
      setSending(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleResend}
      disabled={sending}
      title="Resend invitation"
      className="p-1.5 text-muted-foreground/70 hover:text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50"
    >
      <RefreshCw className={`h-4 w-4 ${sending ? 'animate-spin' : ''}`} />
    </button>
  )
}

// ── Invite Modal ──────────────────────────────────────────────

// InviteModal — replaces the hand-rolled overlay with the DS Modal stack
// so the dialog portals to body and viewport-centers regardless of
// ancestor backdrop-filter / transform / containing-block traps.
function InviteModal({
  orgId,
  onClose,
  onSuccess,
}: {
  orgId: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InvitationRole>('viewer')
  const [sending, setSending] = useState(false)

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

  const handleSend = async () => {
    if (!isValidEmail(email.trim())) return
    setSending(true)
    try {
      await cpClient().post('/api/v1/organizations/{id}/invitations', {
        path: { id: orgId },
        body: { email: email.trim(), role },
      })
      toast.success(`Invitation sent to ${email}`)
      onSuccess()
    } catch (err) {
      toast.error(readErr(err, 'Failed to send invitation'))
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <ModalContent size="sm">
        <ModalHeader>
          <ModalTitle>Invite a member</ModalTitle>
          <ModalDescription>
            Send an email invitation. They'll set their own password after accepting.
          </ModalDescription>
        </ModalHeader>

        <div className="space-y-4 px-6">
          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Email address
            </label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full"
              placeholder="colleague@company.com"
            />
          </div>

          <div>
            <label
              htmlFor="invite-role"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Role
            </label>
            <Select value={role} onValueChange={(v) => setRole(v as InvitationRole)}>
              <SelectTrigger id="invite-role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {INVITE_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground/70">
              {role === 'admin' && 'Full access to manage members, tenants, and settings.'}
              {role === 'viewer' && 'Read-only access to the organization.'}
            </p>
          </div>
        </div>

        <ModalFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={!isValidEmail(email.trim())}
            loading={sending}
            leftIcon={Mail}
          >
            Send invitation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// ── Confirm Modal ─────────────────────────────────────────────
// Thin wrapper around the DS ConfirmDialog so existing call sites
// don't have to change. Migrated from the hand-rolled `fixed inset-0`
// overlay which fell victim to ancestor backdrop-filter capturing the
// viewport-relative reference frame.
function ConfirmModal({
  title,
  description,
  confirmLabel,
  variant = 'danger',
  onClose,
  onConfirm,
}: {
  title: string
  description: React.ReactNode
  confirmLabel: string
  variant?: 'danger' | 'primary'
  onClose: () => void
  onConfirm: () => Promise<void>
}) {
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      destructive={variant === 'danger'}
      onConfirm={onConfirm}
    />
  )
}

// ── Helpers ───────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}
